/**
 * @fileoverview Scout Runs Page
 * @description Landing view listing every imported Scout consolidated report.
 * @module features/ScoutEval/pages/RunsPage
 */

import React from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  Chip,
  IconButton,
  Paper,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  ArrowRightLeft,
  ClipboardList,
  FileText,
  Gauge,
  Radar,
  Trash2,
  TrendingDown,
} from 'lucide-react';
import { DeltaIndicator, EmptyState, ScoreChip, SummaryMetric } from '../components/common';
import { ReportUpload } from '../components/ReportUpload';

/**
 * Render one imported run card.
 * @param {Object} props - Run record, previous run, and card actions.
 * @returns {JSX.Element} Run card.
 */
function RunCard({ run, previous, isLatest, onOpen, onDelete }) {
  const delta = previous ? run.summary.overallAverage - previous.summary.overallAverage : null;

  return (
    <Paper className="scout-run-card" elevation={0}>
      <Box
        className="scout-run-body"
        role="button"
        tabIndex={0}
        onClick={() => onOpen(run)}
        onKeyDown={(event) => {
          if (event.key === 'Enter' || event.key === ' ') {
            event.preventDefault();
            onOpen(run);
          }
        }}
      >
        <Stack direction="row" spacing={1.5} alignItems="flex-start">
          <Box className="scout-run-icon"><FileText size={20} /></Box>
          <Box flex={1} minWidth={0}>
            <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
              <Typography className="scout-run-name">{run.fileName}</Typography>
              {isLatest && <Chip size="small" className="scout-latest-chip" label="Latest" />}
            </Stack>
            <Typography className="scout-run-description">
              {run.generatedAt ? `Generated ${run.generatedAt}` : `Imported ${new Date(run.importedAt).toLocaleString()}`}
            </Typography>
          </Box>
          <Stack alignItems="flex-end" spacing={0.5}>
            <ScoreChip score={run.summary.overallAverage} band={run.summary.overallBand} />
            <DeltaIndicator delta={delta} />
          </Stack>
        </Stack>

        <Stack className="scout-run-chips" direction="row" spacing={0.75} flexWrap="wrap" useFlexGap>
          <Chip size="small" variant="outlined" label={`${run.summary.evaluatorsScored} evaluators`} />
          <Chip size="small" variant="outlined" label={`${run.summary.belowTarget} below target`} />
          <Chip size="small" variant="outlined" label={`${run.groups.length} detail reports`} />
          {run.summary.totalRows > 0 && (
            <Chip size="small" variant="outlined" label={`${run.summary.totalRows} rows evaluated`} />
          )}
        </Stack>
      </Box>

      <Tooltip title="Delete run">
        <IconButton
          size="small"
          className="scout-run-menu"
          aria-label={`Delete ${run.fileName}`}
          onClick={() => onDelete(run.id)}
        >
          <Trash2 size={16} />
        </IconButton>
      </Tooltip>
    </Paper>
  );
}

/**
 * Render the Scout evaluation landing page.
 * @param {Object} props - Stored runs, import state, and handlers.
 * @returns {JSX.Element} Runs page.
 */
export function RunsPage({ runs, isImporting, errorMessage, onImport, onDelete, onDismissError }) {
  const navigate = useNavigate();
  const ordered = [...runs].reverse();
  const latest = runs[runs.length - 1] || null;
  const previous = runs[runs.length - 2] || null;
  const overallDelta = latest && previous
    ? latest.summary.overallAverage - previous.summary.overallAverage
    : null;

  /**
   * Import a report and open its scorecard.
   * @param {File} file - Selected report file.
   * @returns {Promise<void>} Resolves once the import settles.
   */
  const importAndOpen = async (file) => {
    const run = await onImport(file);
    if (run) {
      navigate(run.id);
    }
  };

  return (
    <>
      <Box className="scout-header">
        <Stack direction="row" spacing={1.75} alignItems="flex-start">
          <Box className="scout-header-icon"><Radar size={24} /></Box>
          <Box>
            <Typography variant="h4" component="h1">Scout Evals</Typography>
            <Typography color="text.secondary">
              Import a Scout consolidated report, track every evaluator score, and compare runs over time.
            </Typography>
          </Box>
        </Stack>
        <Stack direction="row" spacing={1}>
          <Button
            variant="outlined"
            startIcon={<ArrowRightLeft size={16} />}
            disabled={runs.length < 2}
            onClick={() => navigate('compare')}
          >
            Compare runs
          </Button>
          <ReportUpload onFile={importAndOpen} busy={isImporting} />
        </Stack>
      </Box>

      {errorMessage && <Alert severity="error" onClose={onDismissError}>{errorMessage}</Alert>}

      {runs.length === 0 ? (
        <ReportUpload onFile={importAndOpen} busy={isImporting} showButton={false}>
          <EmptyState
            icon={FileText}
            title="Import your first Scout report"
            message="Upload a consolidated_summary_report.html produced by the Scout evaluation pipeline. Every evaluator score, detail report, and source file is read from the report itself."
            hint="You can also drop the file anywhere on this panel. Reports are parsed in your browser and only the rollup is stored locally."
          >
            <ReportUpload onFile={importAndOpen} busy={isImporting} label="Upload report" />
          </EmptyState>
        </ReportUpload>
      ) : (
        <>
          <Box className="scout-summary-grid" aria-label="Latest run summary">
            <SummaryMetric
              label="Overall average"
              value={latest.summary.overallAverage.toFixed(2)}
              icon={Gauge}
              color="#01426A"
            >
              <Stack direction="row" spacing={1} alignItems="center">
                <Typography className="scout-metric-caption">vs previous run</Typography>
                <DeltaIndicator delta={overallDelta} />
              </Stack>
            </SummaryMetric>
            <SummaryMetric
              label="Evaluators scored"
              value={latest.summary.evaluatorsScored}
              caption={`${latest.summary.notRun} not run`}
              icon={ClipboardList}
              color="#0097A9"
            />
            <SummaryMetric
              label="Below target"
              value={latest.summary.belowTarget}
              caption="Score below 0.75"
              icon={TrendingDown}
              color="#C81E3A"
            />
            <SummaryMetric
              label="Runs imported"
              value={runs.length}
              caption="Kept in this browser"
              icon={FileText}
              color="#7C3AED"
            />
          </Box>

          <ReportUpload onFile={importAndOpen} busy={isImporting} showButton={false}>
            <Stack spacing={1.5} width="100%">
              {ordered.map((run, position) => (
                <RunCard
                  key={run.id}
                  run={run}
                  previous={ordered[position + 1] || null}
                  isLatest={position === 0}
                  onOpen={(target) => navigate(target.id)}
                  onDelete={onDelete}
                />
              ))}
            </Stack>
          </ReportUpload>
        </>
      )}
    </>
  );
}

export default RunsPage;
